import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Divider,
  Stack,
  Avatar,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Inbox as InboxIcon,
  Link as ConnectorsIcon,
  SmartToy as BotsIcon,
  Notifications as NotificationsIcon,
  TrackChanges as TrackChangesIcon,
  Receipt as ReceiptIcon,
  Storefront as StorefrontIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { OptionsMenu } from '../common/OptionsMenu';
import { K, drawerWidth, sidebarBg, navItemSelectedBg, navItemSelectedBgHover } from './kobraLayoutTokens';

const KOBRA_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/images/logotipo_kobraIA_removebg.png`;

const homeSectionItems = [
  { text: 'Inicio', icon: <HomeIcon />, path: '/home' },
  { text: 'Inbox', icon: <InboxIcon />, path: '/inbox' },
];

const menuItems = [
  { text: 'Bots Management', icon: <BotsIcon />, path: '/bots-management' },
  { text: 'Estrategias de Pago', icon: <NotificationsIcon />, path: '/strategies' },
  { text: 'Seguimiento', icon: <TrackChangesIcon />, path: '/seguimiento' },
  { text: 'Connectors', icon: <ConnectorsIcon />, path: '/connectors' },
  { text: 'Catálogo público', icon: <StorefrontIcon />, path: '/product-catalog' },
  { text: 'Comprobantes', icon: <ReceiptIcon />, path: '/payment-proofs' },
];

const drawerPaperSx = {
  boxSizing: 'border-box',
  width: drawerWidth,
  borderRight: `1px solid ${K.border}`,
  backgroundColor: sidebarBg,
  backgroundImage: 'none',
};

export const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const renderNavItem = (item) => {
    const selected = item.matchPrefix
      ? location.pathname.startsWith(item.matchPrefix)
      : location.pathname === item.path;
    return (
      <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
        <ListItemButton
          selected={false}
          aria-current={selected ? 'page' : undefined}
          onClick={() => {
            navigate(item.path);
            onMobileClose();
          }}
          sx={{
            borderRadius: 999,
            position: 'relative',
            py: 1.15,
            px: 1,
            color: alpha(K.navy, 0.75),
            '& .MuiListItemIcon-root': {
              minWidth: 40,
              color: alpha(K.navy, 0.45),
            },
            '& .MuiListItemText-primary': {
              fontWeight: 600,
              fontSize: '0.875rem',
            },
            '&:hover': {
              bgcolor: selected ? navItemSelectedBgHover : alpha(K.blue, 0.06),
            },
            ...(selected && {
              bgcolor: navItemSelectedBg,
              color: K.blue,
              boxShadow: `0 4px 16px ${alpha(K.blue, 0.28)}`,
              '&:hover': {
                bgcolor: navItemSelectedBgHover,
                boxShadow: `0 6px 20px ${alpha(K.blue, 0.35)}`,
              },
              '& .MuiListItemIcon-root': { color: K.blue },
              '& .MuiListItemText-primary': {
                fontWeight: 700,
                color: K.blue,
              },
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Box
          component="img"
          src={KOBRA_LOGO_SRC}
          alt="Kobra IA by Bankivo"
          sx={{
            width: '100%',
            maxWidth: drawerWidth - 10,
            height: 'auto',
            maxHeight: 110,
            objectFit: 'contain',
            objectPosition: 'left center',
            display: 'block',
          }}
        />
      </Box>
      <Divider sx={{ borderColor: K.border,py: -1.5 }} />
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <List sx={{ px: 1.5, py: 1.5, flexGrow: 1 }}>
          <ListSubheader
            component="div"
            disableSticky
            sx={{
              bgcolor: 'transparent',
              py: 0.5,
              px: 1,
              lineHeight: 1.5,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: alpha(K.navy, 0.45),
            }}
          >
            Inicio
          </ListSubheader>
          {homeSectionItems.map(renderNavItem)}
          <Divider sx={{ borderColor: K.border, my: 1.25, mx: 0.5 }} />
          {menuItems.map(renderNavItem)}
        </List>
      </Box>
      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: 'center',
          borderTop: `1px solid ${K.border}`,
          bgcolor: '#fff',
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: alpha(K.blue, 0.14),
            color: K.blue,
            fontWeight: 800,
            fontSize: '0.95rem',
          }}
        >
          {user?.name?.charAt(0) || 'U'}
        </Avatar>
        <Box sx={{ mr: 'auto', minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.25,
              textAlign: 'left',
              color: K.navy,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.name || 'Usuario'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: alpha(K.navy, 0.45),
              textAlign: 'left',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.email || 'user@bankivo.com'}
          </Typography>
        </Box>
        <OptionsMenu />
      </Stack>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
};
